import React, { useReducer, useEffect } from 'react';
import { css } from '@compiled/react';

import Spinner from '@atlaskit/spinner/spinner';

import { fg } from '@confluence/feature-gating';

import { useStaticMacroRenderAnalytics } from '../hooks/useStaticMacroRenderAnalytics.ts';

import { useStaticMacroStoreContext } from './StaticMacroStoreContext.tsx';

const spinnerWrapperStyles = css({ overflow: 'hidden' });

// How long a newly inserted macro waits for its insertion to be saved before falling back to the iframe.
export const INSERTION_DRAFT_SYNC_WAIT_MS = 30_000;

export const POST_EDIT_DRAFT_SYNC_WAIT_MS = 30_000;

type ForgeMacroProps = {
	localId?: string;
	extensionKey?: string;
	guestParams?: unknown;
	renderIframe: () => React.ReactElement;
	isEditing?: boolean;
	isLivePage?: boolean;
	// True for an untouched Connect macro (extensionType `com.atlassian.confluence.macro.core`).
	isConnectShapedNode?: boolean;
	nestedMacroLocalIds?: string[];
	preloadedHtml?: string;
};

function getNestedMacroLocalIds(node: any): string[] {
	const localIds = new Set<string>();
	const visit = (nodes: unknown) => {
		const childNodes = Array.isArray(nodes)
			? nodes
			: typeof nodes === 'object' && nodes !== null && Array.isArray((nodes as any).content)
				? (nodes as any).content
				: [];
		childNodes.forEach((child: any) => {
			const type = typeof child.type === 'string' ? child.type : child.type?.name;
			if (['extension', 'bodiedExtension', 'inlineExtension'].includes(type)) {
				const localId =
					child.localId ??
					child.attrs?.localId ??
					child.parameters?.localId ??
					child.attrs?.parameters?.localId;
				if (localId) {
					localIds.add(localId);
				}
			}
			visit(child.content);
		});
	};
	visit(node?.content);
	return Array.from(localIds);
}

export function createForgeMacroExtensionHandler(
	isEditing?: boolean,
	isLivePage?: boolean,
):
	| ((node: any, renderFn: (param: { references: any[] }) => JSX.Element) => JSX.Element)
	| undefined {
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (!fg('conf_frontend_forge_static_macro_mvp')) {
		return undefined;
	}

	return (node: any, renderFn: (param: { references: any[] }) => JSX.Element): JSX.Element => (
		<ForgeMacro
			{...getForgeMacroNodeProps(node)}
			renderIframe={() => renderFn({ references: [] })}
			isEditing={isEditing}
			isLivePage={isLivePage}
		/>
	);
}

/** The `ForgeMacro` props derived from an ADF extension node, shared by every handler that renders it. */
export function getForgeMacroNodeProps(
	node: any,
): Pick<
	ForgeMacroProps,
	'localId' | 'extensionKey' | 'guestParams' | 'isConnectShapedNode' | 'nestedMacroLocalIds'
> {
	// Untouched Connect macros keep extensionType `com.atlassian.confluence.macro.core` and are
	// keyed in `confluence_staticMacros` by `parameters.macroMetadata.macroId`, not `localId`.
	const isConnectShapedNode = node?.extensionType === 'com.atlassian.confluence.macro.core';
	return {
		localId: isConnectShapedNode
			? node?.parameters?.macroMetadata?.macroId?.value
			: (node?.localId ?? node?.parameters?.localId),
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		extensionKey: node?.extensionKey?.split('/').at(-1),
		guestParams: node?.parameters?.guestParams || node?.parameters?.config,
		isConnectShapedNode,
		nestedMacroLocalIds: getNestedMacroLocalIds(node),
	};
}

export function ForgeMacro({
	localId,
	extensionKey,
	guestParams,
	renderIframe,
	isEditing,
	isLivePage,
	isConnectShapedNode,
	nestedMacroLocalIds = [],
	preloadedHtml,
}: ForgeMacroProps): React.ReactElement {
	const ctx = useStaticMacroStoreContext();
	const store = ctx?.storeRef.current;
	const [, forceUpdate] = useReducer((n: number) => n + 1, 0);
	const [isRefetching, setIsRefetching] = React.useState(false);
	const readyEventEmittedRef = React.useRef(false);

	// Check if this extension supports staticView, based on preloaded extension data.
	// extensionsLoaded is true when __SSR_FORGE_EXTENSIONS__ has been populated.
	//
	// Native Forge extensions resolve via their own extensionKey (ctx.isStaticExtension).
	// Connect-shaped nodes resolve against the SAME preloaded data using their legacy
	// extensionKey — matched either via the adopted extension's own key (same-key adoption)
	// or its migratedFrom aliases (different-key adoption) — so a Connect-shaped node whose
	// adopted extension is confirmed non-static can fall back to the iframe immediately, once
	// extensions have loaded, without waiting for the bulk staticMacros query at all.
	//
	// If no match is found yet (extensions not loaded, or genuinely unrecognized), treat the
	// node as "maybe static" and rely on store.fullyLoaded below: a Connect-shaped node is
	// never newly inserted, so a missing static result once the bulk query completes means it
	// isn't static.
	const isKnownStaticExtension = !!extensionKey && !!ctx?.isStaticExtension(extensionKey);
	const isKnownNonStaticExtension =
		!!isConnectShapedNode &&
		!!extensionKey &&
		!!ctx?.isKnownExtension(extensionKey) &&
		!isKnownStaticExtension;

	const hasStaticResult = !!localId && (!!store?.html.has(localId) || !!store?.errors.has(localId));
	const mightBeStaticConnectMacro =
		!!isConnectShapedNode && !isKnownNonStaticExtension && (!store?.fullyLoaded || hasStaticResult);

	const isStatic = isKnownStaticExtension || mightBeStaticConnectMacro;
	const extensionsLoaded = !!window.__SSR_FORGE_EXTENSIONS__?.['xen:macro']?.extensions?.length;

	useEffect(() => {
		if (!ctx || !localId) return;
		const handleUpdate = ({
			type,
		}: {
			type: 'static-macro-result' | 'fully-loaded' | 'refetch-exhausted';
		}) => {
			// Only an update for this macro completes a refetch. Bulk-query completion must not end a
			// pending refetch started by a parameter change.
			if (type !== 'fully-loaded') {
				setIsRefetching(false);
			}
			forceUpdate();
		};
		const unsubscribe = ctx.subscribe(localId, handleUpdate);
		// Data may have already arrived before we subscribed — check immediately.
		if (store?.html.has(localId) || store?.errors.has(localId)) {
			handleUpdate({ type: 'static-macro-result' });
		} else if (store?.fullyLoaded) {
			handleUpdate({ type: 'fully-loaded' });
		}
		return unsubscribe;
	}, [ctx, localId, forceUpdate, store]);

	useEffect(() => {
		if (ctx && localId && !ctx.storeRef.current?.fullyLoaded) {
			ctx.markPresentAtLoad(localId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// The bridge sends one request and, if it brings back no HTML, one retry. The spinner stops when
	// either returns a result or both come back empty.
	const startPostEditRefetch = React.useCallback(() => {
		if (!ctx || !localId) return;
		setIsRefetching(true);
		ctx.triggerRefetch(localId, 'edit');
	}, [ctx, localId]);

	const deferPostEditRefetchUntilDraftSync = React.useCallback(() => {
		if (!ctx || !localId) return undefined;
		return ctx.deferUntilDraftSync(localId, startPostEditRefetch);
	}, [ctx, localId, startPostEditRefetch]);

	const showSpinnerAndDeferRefetchUntilDraftSync = React.useCallback(() => {
		if (!ctx || !localId) return undefined;
		setIsRefetching(true);
		const spinnerTimeoutId = setTimeout(() => setIsRefetching(false), POST_EDIT_DRAFT_SYNC_WAIT_MS);
		const removeDeferredRefetch = ctx.deferUntilDraftSync(localId, () => {
			clearTimeout(spinnerTimeoutId);
			startPostEditRefetch();
		});
		ctx.requestDraftSync();

		return () => {
			removeDeferredRefetch();
			clearTimeout(spinnerTimeoutId);
		};
	}, [ctx, localId, startPostEditRefetch]);

	useEffect(() => {
		if (!ctx || !localId || !isStatic) {
			return undefined;
		}
		if (!isEditing || !isLivePage) {
			return undefined;
		}
		// Editors can remount node views without any change, e.g. while loading. Only a remount after
		// a draft sync, such as finishing a body edit, means the macro may have changed.
		const isRemountAfterDraftSync =
			ctx.hasMountedBefore(localId) && ctx.hasDraftSyncedSinceLastMount(localId);
		ctx.markMounted(localId);
		if (!isRemountAfterDraftSync) {
			return undefined;
		}
		if (nestedMacroLocalIds.length > 0) {
			startPostEditRefetch();
			return undefined;
		}
		const removeDeferredRefetch = deferPostEditRefetchUntilDraftSync();
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Retain the gate for this change at the developer's request.
		if (fg('forge_static_macros_ux_imp')) {
			ctx.requestDraftSync();
		}
		return removeDeferredRefetch;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const guestParamsRef = React.useRef<string | undefined>(undefined);
	const serializedGuestParams = React.useMemo(
		() => JSON.stringify(guestParams ?? null),
		[guestParams],
	);

	const errorCode = localId ? store?.errors.get(localId) : undefined;
	const html =
		(localId ? store?.html.get(localId) : undefined) ??
		(localId && errorCode === undefined ? preloadedHtml : undefined);
	const resultMetadata = localId ? store?.resultMetadata.get(localId) : undefined;

	useStaticMacroRenderAnalytics({
		html,
		errorCode,
		result: resultMetadata,
		isEditing: Boolean(isEditing),
		isLivePage: Boolean(isLivePage),
	});

	// Signal cc-puppeteer that this static macro has finished rendering.
	// cc-puppeteer's waitForAllExtensionReadyEvents() waits for EXTENSION_READY from every
	// Forge extension it finds in the ADF (regardless of emitsReadyEvent manifest declaration).
	// Without this signal, the full 10s timeout is consumed on every PDF export with a static macro.
	// Guarded by a ref so it only fires once per ForgeMacro instance.
	useEffect(() => {
		if (html && localId && !readyEventEmittedRef.current) {
			readyEventEmittedRef.current = true;
			window.postMessage(
				{ source: 'forge-extension', type: 'EXTENSION_READY', localId },
				window.location.origin,
			);
		}
	}, [html, localId]);

	// When a config changes, we want to trigger a re-render.
	useEffect(() => {
		if (!ctx || !localId || !isStatic || !isEditing) return undefined;
		const serialized = serializedGuestParams;
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Retain the gate for this change at the developer's request.
		if (fg('forge_static_macros_ux_imp')) {
			const prev = ctx.rememberGuestParams(localId, serialized);
			// Skip on first render for this macro instance — only refetch on subsequent changes.
			if (prev === undefined || prev === serialized) return undefined;
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- The rule only detects cleanups returned at the top level of the effect.
			return showSpinnerAndDeferRefetchUntilDraftSync();
		} else {
			const prev = guestParamsRef.current;
			guestParamsRef.current = serialized;
			// Skip on first render for this macro instance — only refetch on subsequent changes.
			if (prev === undefined || prev === serialized) return undefined;
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- The rule only detects cleanups returned at the top level of the effect.
			return deferPostEditRefetchUntilDraftSync();
		}
	}, [
		ctx,
		localId,
		isStatic,
		isEditing,
		serializedGuestParams,
		deferPostEditRefetchUntilDraftSync,
		showSpinnerAndDeferRefetchUntilDraftSync,
	]);

	const isFullyLoaded = !!store?.fullyLoaded;
	useEffect(() => {
		if (!ctx || !localId || !isStatic || !isEditing) return undefined;
		if (!isFullyLoaded) return undefined;
		if (html !== undefined || errorCode !== undefined) return undefined;

		// Macros that were on the page when it loaded weren't inserted. Without a result, they're not static.
		if (ctx.wasPresentAtLoad(localId)) return undefined;

		const currentStore = ctx.storeRef.current;
		if (
			!currentStore ||
			currentStore.insertionRefetchGaveUp.has(localId) ||
			currentStore.insertionRefetchStarted.has(localId)
		) {
			return undefined;
		}

		// Don't request anything until the insertion has been saved. If the request and its retry come
		// back empty, the store marks the insertion as given up.
		const giveUpTimeoutId = setTimeout(() => {
			currentStore.insertionRefetchGaveUp.add(localId);
			forceUpdate();
		}, INSERTION_DRAFT_SYNC_WAIT_MS);
		const removeDeferredRefetch = ctx.deferUntilDraftSync(localId, () => {
			clearTimeout(giveUpTimeoutId);
			currentStore.insertionRefetchStarted.add(localId);
			ctx.triggerRefetch(localId, 'insertion');
		});
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Retain the gate for this change at the developer's request.
		if (fg('forge_static_macros_ux_imp')) {
			ctx.requestDraftSync();
		}

		return () => {
			removeDeferredRefetch();
			clearTimeout(giveUpTimeoutId);
		};
	}, [ctx, localId, isStatic, isEditing, isFullyLoaded, html, errorCode, forceUpdate]);

	if (!ctx || !localId || !store) {
		return renderIframe();
	}

	if (isRefetching) {
		return (
			<div css={spinnerWrapperStyles}>
				<Spinner size="small" />
			</div>
		);
	}

	if (html) {
		return (
			<div
				data-testid="forge-static-macro-content"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		);
	}

	if (errorCode) {
		return renderIframe();
	}

	// Once the query is fully loaded, any macro without html/error is not a static macro → iframe.
	// Also fall back immediately if we KNOW this extension doesn't support staticView —
	// only treat !isStatic as definitive when extensions have actually been loaded.
	// If extensions aren't loaded yet, show spinner and wait (preloader may still be in-flight).
	const isAwaitingInsertedContent =
		isStatic &&
		isEditing &&
		!ctx.wasPresentAtLoad(localId) &&
		!store.insertionRefetchGaveUp.has(localId);

	if ((store.fullyLoaded || (extensionsLoaded && !isStatic)) && !isAwaitingInsertedContent) {
		return renderIframe();
	}

	// Extension supports staticView but data hasn't arrived yet — show spinner briefly.
	// Use the same testid as the iframe loader so cc-puppeteer waits for it to disappear.
	return (
		<div css={spinnerWrapperStyles} data-testid="forge-app-loading-spinner">
			<Spinner size="small" />
		</div>
	);
}
